import { useState } from "react";
import { GraduationCap, Medal, HardHat, ChevronDown, Code2 } from "lucide-react";

const leaders = [
  {
    id: "ceo",
    name: "Jary Castro",
    role: "Chief Executive Officer",
    img: "/assets/ceo.jpg",
    accent: "#00f0ff",
    summary: "Computer scientist leading Gsoftware's technical vision and product engineering.",
    bio: [
      {
        icon: GraduationCap,
        title: "Computer Scientist — FGCU",
        text: "Jary earned his Computer Science degree at Florida Gulf Coast University, building deep expertise in software engineering, systems, and modern application architecture.",
      },
      {
        icon: Code2,
        title: "Technical Founder",
        text: "He leads Gsoftware's engineering direction — from the websites that win clients to the apps that automate entire business workflows for hospitals and restaurants.",
      },
    ],
  },
  {
    id: "coo",
    name: "Damian Gonzalez",
    role: "Chief Operating Officer",
    img: "/assets/coo.jpg",
    accent: "#ff003c",
    summary: "Operations leader with military discipline, medical training, and real-world business execution.",
    bio: [
      {
        icon: Medal,
        title: "Military Background",
        text: "Years of military service forged the discipline, precision, and mission-first execution he brings to every Gsoftware operation and client engagement.",
      },
      {
        icon: GraduationCap,
        title: "Medical Studies — ASU",
        text: "He holds a degree in Medical Studies from Arizona State University, giving Gsoftware genuine insight into how hospitals and healthcare teams actually work.",
      },
      {
        icon: HardHat,
        title: "Construction Company Owner",
        text: "He spent years running a construction company — managing crews, budgets, and deadlines — experience that now drives how Gsoftware delivers projects on time and on spec.",
      },
    ],
  },
];

export default function Team() {
  const [open, setOpen] = useState(null);

  return (
    <section id="leadership" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#00f0ff]">// Leadership</p>
          <h2 className="font-display mt-4 text-3xl font-black tracking-tight sm:text-4xl">
            The people behind <span className="text-[#ff003c] text-glow-magenta">the machine</span>
          </h2>
        </div>

        <div className="mt-16 grid gap-8 md:grid-cols-2">
          {leaders.map((l) => {
            const isOpen = open === l.id;
            return (
              <article
                key={l.id}
                data-testid={`team-card-${l.id}`}
                className="border border-white/10 bg-[#0a0a0a] transition-colors duration-300 hover:border-white/25"
              >
                <div className="flex flex-col gap-6 p-8 sm:flex-row lg:p-10">
                  <div className="relative h-32 w-32 shrink-0">
                    <div
                      className="absolute -inset-1.5 rounded-sm opacity-60"
                      style={{ border: `1px solid ${l.accent}`, boxShadow: `0 0 16px ${l.accent}44` }}
                    />
                    <img
                      src={l.img}
                      alt={`${l.name} portrait`}
                      className="relative h-full w-full rounded-sm object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em]" style={{ color: l.accent }}>
                      {l.role}
                    </p>
                    <h3 className="font-display mt-2 text-xl font-bold">{l.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/50">{l.summary}</p>
                    <button
                      onClick={() => setOpen(isOpen ? null : l.id)}
                      data-testid={`team-bio-toggle-${l.id}`}
                      className="font-mono-tech mt-5 flex items-center gap-2 border border-white/20 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-white/80 transition-colors duration-300 hover:text-white"
                      style={isOpen ? { borderColor: l.accent, color: l.accent } : undefined}
                      aria-expanded={isOpen}
                    >
                      {isOpen ? "Hide Background" : "View Background"}
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                  </div>
                </div>

                <div
                  className={`grid transition-[grid-template-rows] duration-500 ease-out ${
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="space-y-5 border-t border-white/10 p-8 lg:px-10" data-testid={`team-bio-${l.id}`}>
                      {l.bio.map((b) => (
                        <div key={b.title} className="flex gap-4">
                          <div
                            className="flex h-10 w-10 shrink-0 items-center justify-center border"
                            style={{ borderColor: `${l.accent}55` }}
                          >
                            <b.icon size={17} style={{ color: l.accent }} strokeWidth={1.75} />
                          </div>
                          <div>
                            <p className="font-display text-sm font-bold">{b.title}</p>
                            <p className="mt-1.5 text-sm leading-relaxed text-white/50">{b.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
